import { Alert, Button, Card, Col, DatePicker, Divider, Form, Input, InputNumber, Row, Space } from "antd";
import { useState } from "react";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { toast } from "react-toastify";
import { CreditCardOutlined, EditOutlined, SaveOutlined } from "@ant-design/icons";
import { displayNumbers } from "../../../../variables/numbersFunctions";
import dayjs from "dayjs";

export const ChequeRetrocession = ({ retrocession, onUpdate }) => {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const hasCheque = retrocession.numeroCheque && retrocession.banque;

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);

            await axiosInstance.put(
                `/api/retrocession/${retrocession.retrocessionId}/cheque`,
                {
                    banque: values.banque,
                    numeroCheque: values.numeroCheque,
                    dateCheque: values.dateCheque?.format('YYYY-MM-DD'),
                    montantCheque: values.montantCheque
                }
            );

            toast.success('Chèque enregistré avec succès');
            setEditing(false);
            if (onUpdate) onUpdate();
        } catch (err) {
            if (err.errorFields) return; // Erreur de validation du formulaire
            console.error(err);
            toast.error("Erreur lors de l'enregistrement du chèque");
        } finally {
            setSaving(false);
        }
    };

    const startEdit = () => {
        form.setFieldsValue({
            banque: retrocession.banque,
            numeroCheque: retrocession.numeroCheque,
            dateCheque: retrocession.dateCheque ? dayjs(retrocession.dateCheque) : null,
            montantCheque: retrocession.montantCheque
        });
        setEditing(true);
    };

    // --- Mode affichage (chèque déjà renseigné) ---
    if (hasCheque && !editing) {
        return (
            <>
                <Divider>
                    <CreditCardOutlined /> Chèque de Rétrocession
                </Divider>
                <Card
                    size="small"
                    style={{
                        borderLeft: '4px solid #52c41a',
                        background: '#f6ffed',
                        marginBottom: 20
                    }}
                    extra={
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={startEdit}
                        >
                            Modifier
                        </Button>
                    }
                >
                    <Row gutter={[16, 12]}>
                        <Col span={6}>
                            <div style={{ color: '#888', fontSize: 12 }}>Banque</div>
                            <div style={{ fontWeight: 600 }}>{retrocession.banque}</div>
                        </Col>
                        <Col span={6}>
                            <div style={{ color: '#888', fontSize: 12 }}>N° Chèque</div>
                            <div style={{ fontWeight: 600 }}>{retrocession.numeroCheque}</div>
                        </Col>
                        <Col span={6}>
                            <div style={{ color: '#888', fontSize: 12 }}>Date d'émission</div>
                            <div style={{ fontWeight: 600 }}>
                                {retrocession.dateCheque
                                    ? dayjs(retrocession.dateCheque).format('DD/MM/YYYY')
                                    : '-'}
                            </div>
                        </Col>
                        <Col span={6}>
                            <div style={{ color: '#888', fontSize: 12 }}>Montant</div>
                            <div style={{ fontWeight: 600, color: '#52c41a' }}>
                                {displayNumbers(retrocession.montantCheque || 0)} F CFA
                            </div>
                        </Col>
                    </Row>
                </Card>
            </>
        );
    }

    // --- Mode formulaire (nouveau ou édition) ---
    return (
        <>
            <Divider>
                <CreditCardOutlined /> Chèque de Rétrocession
            </Divider>
            {!hasCheque && !editing && (
                <Alert
                    message="Aucun chèque renseigné pour cette rétrocession"
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    action={
                        <Button
                            type="primary"
                            size="small"
                            onClick={() => {
                                form.resetFields();
                                setEditing(true);
                            }}
                        >
                            Renseigner le chèque
                        </Button>
                    }
                />
            )}
            {(editing) && (
                <Card
                    size="small"
                    style={{
                        borderLeft: '4px solid #1890ff',
                        background: '#f0f5ff',
                        marginBottom: 20
                    }}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        initialValues={{
                            banque: retrocession.banque,
                            numeroCheque: retrocession.numeroCheque,
                            dateCheque: retrocession.dateCheque ? dayjs(retrocession.dateCheque) : null,
                            montantCheque: retrocession.montantCheque
                        }}
                    >
                        <Row gutter={16}>
                            <Col span={6}>
                                <Form.Item
                                    name="banque"
                                    label="Banque"
                                    rules={[{ required: true, message: 'Requis' }]}
                                >
                                    <Input placeholder="Ex: ECOBANK" />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    name="numeroCheque"
                                    label="N° Chèque"
                                    rules={[{ required: true, message: 'Requis' }]}
                                >
                                    <Input placeholder="Ex: CHQ-00123" />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    name="dateCheque"
                                    label="Date d'émission"
                                    rules={[{ required: true, message: 'Requis' }]}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        format="DD/MM/YYYY"
                                        placeholder="Sélectionner"
                                        disabledDate={current => current && current > dayjs().endOf('day')} // Interdire les dates futures
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    name="montantCheque"
                                    label="Montant (F CFA)"
                                    rules={[{ required: true, message: 'Requis' }]}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                        parser={value => value.replace(/\s/g, '')}
                                        placeholder="0"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Space>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={handleSave}
                                loading={saving}
                            >
                                Enregistrer
                            </Button>
                            <Button onClick={() => setEditing(false)}>
                                Annuler
                            </Button>
                        </Space>
                    </Form>
                </Card>
            )}
        </>
    );
};